import {
  CHART_EXPENSES,
  CHART_INCOME,
  CHART_NA,
  CHART_INVESTMENTS_FINANCE,
  CHART_INVESTMENTS_PROPERTY,
  CHART_MORTGAGE_LUMP,
  CHART_REFUNDS,
  CHART_REVERTED,
  CHART_TRANSFERS,
  CHART_UNKNOWN,
  cashflowOfCategory,
  expenseGroupOfCategory,
  groupNameOf,
  investmentPropertyFilterNames,
} from '../utils/personalFinancesHousehold';

export const CURRENT_YEAR = String(new Date().getFullYear());

export const PF_URL = {
  tab: 'tab',
  year: 'year',
  month: 'month',
  q: 'q',
  type: 'type',
  ending: 'ending',
  cf: 'cf',
  grp: 'grp',
  cat: 'cat',
  allocation: 'allocation',
  account: 'account',
} as const;

const LEGACY_URL = {
  side: 'side',
  group: 'group',
  category: 'category',
  omheSide: 'omhe_side',
  omheType: 'omhe_type',
} as const;

export type PersonalTab = 'seco' | 'omhe';

export const ALL_PERSONAL_TABS: PersonalTab[] = ['seco', 'omhe'];

export function allowedPersonalTabs(user?: {
  role?: string;
  personal_households?: string[] | null;
} | null): PersonalTab[] {
  const fromUser = (user?.personal_households || []).filter(
    (value): value is PersonalTab => value === 'seco' || value === 'omhe'
  );
  if (fromUser.length > 0) return fromUser;
  return [...ALL_PERSONAL_TABS];
}

export function initialPersonalTab(
  urlTab: PersonalTab,
  user?: { role?: string; personal_households?: string[] | null } | null
): PersonalTab {
  const allowed = allowedPersonalTabs(user);
  return allowed.includes(urlTab) ? urlTab : allowed[0];
}

export type PersonalUrlFilters = {
  tab: PersonalTab;
  year: string;
  month: string;
  q: string;
  type: string;
  ending: string;
  side: string;
  group: string;
  category: string;
  allocation: string;
  account: string;
};

export const CASHFLOW_URL_LABELS = [
  CHART_INCOME,
  CHART_EXPENSES,
  CHART_TRANSFERS,
  CHART_INVESTMENTS_PROPERTY,
  CHART_INVESTMENTS_FINANCE,
  CHART_MORTGAGE_LUMP,
  'Investments',
  CHART_REFUNDS,
  CHART_REVERTED,
  CHART_UNKNOWN,
  CHART_NA,
];

const ALLOCATION_VALUES = new Set(['personal', 'business', 'n/a']);

export const defaultPersonalUrlFilters = (): PersonalUrlFilters => ({
  tab: 'seco',
  year: '',
  month: '',
  q: '',
  type: '',
  ending: '',
  side: '',
  group: '',
  category: '',
  allocation: '',
  account: '',
});

export const normalizePersonalMonth = (raw: string, year = '') => {
  const text = String(raw || '').trim();
  if (/^\d{4}-\d{2}$/.test(text)) return text;
  if (/^\d{1,2}$/.test(text) && /^\d{4}$/.test(String(year || '').trim())) {
    return `${year}-${String(Number(text)).padStart(2, '0')}`;
  }
  return '';
};

export const postedDateMatchesPersonalMonth = (
  posted: string | null | undefined,
  month: string
) => {
  const key = normalizePersonalMonth(month);
  if (!key) return true;
  return String(posted || '').replace(/T.*/, '').slice(0, 7) === key;
};

export const personalMonthLabel = (month: string) => {
  const key = normalizePersonalMonth(month);
  if (!key) return '';
  const year = key.slice(0, 4);
  const monthNum = Number(key.slice(5, 7));
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[monthNum - 1] || key.slice(5)} ${year.slice(2)}`;
};

export const slugifyHouseholdLabel = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const splitHouseholdUrlValues = (raw: string) =>
  String(raw || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

export const joinHouseholdUrlValues = (values: string[]) =>
  values.map((value) => String(value || '').trim()).filter(Boolean).join(',');

export const slugifyHouseholdUrlList = (value: string) =>
  splitHouseholdUrlValues(value).map(slugifyHouseholdLabel).filter(Boolean).join(',');

export const resolveHouseholdLabel = (raw: string, candidates: Iterable<string>) => {
  const slug = slugifyHouseholdLabel(raw);
  if (!slug) return '';
  const names = Array.from(candidates);
  for (let i = 0; i < names.length; i += 1) {
    const candidate = names[i];
    if (slugifyHouseholdLabel(candidate) === slug) return candidate;
  }
  return raw.trim();
};

export const resolveHouseholdLabels = (raw: string, candidates: Iterable<string>) =>
  splitHouseholdUrlValues(raw)
    .map((part) => resolveHouseholdLabel(part, candidates))
    .filter(Boolean);

export const isHouseholdMultiSelectEvent = (
  event?: { metaKey?: boolean; ctrlKey?: boolean } | null
) => Boolean(event?.metaKey || event?.ctrlKey);

export const toggleHouseholdChartSelection = (selected: string[], name: string) => {
  const label = String(name || '').trim();
  if (!label) return selected;
  return selected.includes(label)
    ? selected.filter((item) => item !== label)
    : [...selected, label];
};

export type HouseholdChartSelection = {
  side: string;
  group: string;
  selected: string[];
};

export const householdChartSelectionFromFilters = (
  filters: PersonalUrlFilters,
  labels: {
    cashflow: string[];
    groups: string[];
    categories: string[];
    catalog?: Record<string, string>;
  }
): HouseholdChartSelection => {
  const catalog = labels.catalog || {};
  const categories = resolveHouseholdLabels(filters.category, labels.categories);
  const groups = resolveHouseholdLabels(filters.group, labels.groups);
  const sides = resolveHouseholdLabels(filters.side, labels.cashflow);
  const side = sides.length === 1 ? sides[0] : '';
  if (categories.length) {
    const first = categories[0];
    const inferredSide = side || cashflowOfCategory(first, catalog);
    const group =
      inferredSide === CHART_EXPENSES
        ? householdUrlGroup(groups.length === 1 ? groups[0] : '', first, catalog, inferredSide)
        : '';
    return { side: inferredSide, group, selected: categories };
  }
  if (groups.length > 1) {
    return { side: CHART_EXPENSES, group: '', selected: groups };
  }
  if (groups.length === 1) {
    return { side: side || CHART_EXPENSES, group: groups[0], selected: [] };
  }
  if (sides.length > 1) {
    return { side: '', group: '', selected: sides };
  }
  return { side, group: '', selected: [] };
};

export const householdCategoryFromChartSelection = (selection: HouseholdChartSelection) => {
  const atCategoryLevel =
    Boolean(selection.side) && (selection.side !== CHART_EXPENSES || Boolean(selection.group));
  return atCategoryLevel && selection.selected.length === 1 ? selection.selected[0] : '';
};

export const householdUrlFiltersFromChart = (chart: {
  side: string;
  group: string;
  selected: string[];
  catalog: Record<string, string>;
}) => {
  const selected = chart.selected.map((name) => String(name || '').trim()).filter(Boolean);
  const atCategoryLevel = Boolean(chart.side) && (chart.side !== CHART_EXPENSES || Boolean(chart.group));
  if (!chart.side && selected.length) {
    return { side: joinHouseholdUrlValues(selected), group: '', category: '' };
  }
  if (chart.side === CHART_EXPENSES && !chart.group && selected.length) {
    return {
      side: CHART_EXPENSES,
      group: joinHouseholdUrlValues(selected),
      category: '',
    };
  }
  const category = atCategoryLevel ? joinHouseholdUrlValues(selected) : '';
  return {
    side: chart.side,
    group: householdUrlGroup(chart.group, category, chart.catalog, chart.side),
    category,
  };
};

export const householdUrlLabelsFromCatalog = (groups: Record<string, string>) => ({
  cashflow: CASHFLOW_URL_LABELS,
  groups: Array.from(new Set(Object.values(groups))),
  categories: [...Object.keys(groups), ...investmentPropertyFilterNames(groups)],
  catalog: groups,
});

/**
 * Group is a URL/drill step only under Expenses (Living, Insurance, Refunds, Reverted, Unknown debits, …).
 * Income / Transfers / Investments (Property) / Investments (Finance) / 3GD Mortgage lump-sum / Unknown cashflows go straight to category, so
 * `grp` must not repeat the cashflow name or the category (e.g. not `cf=income&grp=income`).
 * Legacy `cf=refunds` / `cf=reverted` remap onto Expenses plus that group.
 * Legacy `cf=investments` remaps to Investments (Property).
 */
export const householdUrlGroup = (
  group: string,
  category: string,
  catalog: Record<string, string>,
  cashflow = ''
) => {
  const side = String(cashflow || '').trim();
  if (side && side !== CHART_EXPENSES) return '';

  const explicit = String(group || '').trim();
  const cat = String(category || '').trim();
  const inferred = cat ? groupNameOf(cat, catalog) : '';
  const candidate = explicit || inferred;
  if (!candidate || candidate === CHART_EXPENSES) return '';

  if (side === CHART_EXPENSES) return candidate;
  if (cat && cashflowOfCategory(cat, catalog) === CHART_EXPENSES) {
    return expenseGroupOfCategory(cat, catalog) || candidate;
  }
  if (!cat && explicit && !CASHFLOW_URL_LABELS.includes(explicit)) return explicit;
  return '';
};

export const applyHouseholdUrlLabels = (
  filters: PersonalUrlFilters,
  labels: {
    cashflow: string[];
    groups: string[];
    categories: string[];
    catalog?: Record<string, string>;
  }
): PersonalUrlFilters => {
  const category = resolveHouseholdLabel(filters.category, labels.categories);
  let side = resolveHouseholdLabel(filters.side, labels.cashflow);
  const namedGroup = resolveHouseholdLabel(filters.group, labels.groups);
  if (side === CHART_REFUNDS || side === CHART_REVERTED) {
    const refundGroup = namedGroup || side;
    return {
      ...filters,
      side: CHART_EXPENSES,
      group: labels.catalog
        ? householdUrlGroup(refundGroup, category, labels.catalog, CHART_EXPENSES)
        : refundGroup,
      category,
    };
  }
  if (side === 'Investments') {
    side = CHART_INVESTMENTS_PROPERTY;
  }
  let resolvedCategory = category;
  if (resolvedCategory === CHART_MORTGAGE_LUMP) {
    if (!side || side === CHART_INCOME) side = CHART_MORTGAGE_LUMP;
  }
  const group = labels.catalog
    ? householdUrlGroup(namedGroup, resolvedCategory, labels.catalog, side)
    : side && side !== CHART_EXPENSES
      ? ''
      : namedGroup;
  return {
    ...filters,
    side,
    group,
    category: resolvedCategory,
  };
};

const readText = (params: URLSearchParams, key: string) => String(params.get(key) || '').trim();

export const readPersonalUrlFilters = (
  search = typeof window === 'undefined' ? '' : window.location.search
): PersonalUrlFilters => {
  const params = new URLSearchParams(search);
  const allocation = readText(params, PF_URL.allocation).toLowerCase();
  return {
    tab: params.get(PF_URL.tab) === 'omhe' ? 'omhe' : 'seco',
    year: String(params.get(PF_URL.year) || ''),
    month: normalizePersonalMonth(readText(params, PF_URL.month), String(params.get(PF_URL.year) || '')),
    q: readText(params, PF_URL.q),
    type: readText(params, PF_URL.type) || readText(params, LEGACY_URL.omheType),
    ending: readText(params, PF_URL.ending),
    side:
      readText(params, PF_URL.cf) ||
      readText(params, LEGACY_URL.side) ||
      readText(params, LEGACY_URL.omheSide),
    group: readText(params, PF_URL.grp) || readText(params, LEGACY_URL.group),
    category: readText(params, PF_URL.cat) || readText(params, LEGACY_URL.category),
    allocation: ALLOCATION_VALUES.has(allocation) ? allocation : '',
    account: readText(params, PF_URL.account),
  };
};

const setOrDelete = (params: URLSearchParams, key: string, value: string) => {
  if (value) params.set(key, value);
  else params.delete(key);
};

export const writePersonalUrlFilters = (filters: PersonalUrlFilters) => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const params = url.searchParams;
  params.set(PF_URL.tab, filters.tab);
  setOrDelete(params, PF_URL.year, filters.year);
  setOrDelete(params, PF_URL.month, normalizePersonalMonth(filters.month, filters.year));
  setOrDelete(params, PF_URL.q, filters.q.trim());
  setOrDelete(params, PF_URL.type, filters.type);
  setOrDelete(params, PF_URL.ending, filters.ending);
  setOrDelete(params, PF_URL.cf, slugifyHouseholdUrlList(filters.side));
  setOrDelete(params, PF_URL.grp, slugifyHouseholdUrlList(filters.group));
  setOrDelete(params, PF_URL.cat, slugifyHouseholdUrlList(filters.category));
  setOrDelete(params, PF_URL.allocation, filters.allocation);
  setOrDelete(params, PF_URL.account, filters.account);
  params.delete(LEGACY_URL.side);
  params.delete(LEGACY_URL.group);
  params.delete(LEGACY_URL.category);
  params.delete(LEGACY_URL.omheSide);
  params.delete(LEGACY_URL.omheType);
  const next = `${url.pathname}?${params.toString()}${url.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (current !== next) {
    window.history.replaceState({}, '', next);
  }
};

export const patchPersonalUrlFilters = (patch: Partial<PersonalUrlFilters>) => {
  writePersonalUrlFilters({ ...readPersonalUrlFilters(), ...patch });
};
